import React, {useCallback} from 'react'
import {Col, ButtonSecondary, ProgressButton, Row} from '@modifi/ui'
import {useTranslation} from '@modifi/plugin-i18n'
import {omit} from 'ramda'

import {FormFooterContainer, BaseFormFooterContainer} from '../../../../ui/form/styles'
import {FooterRow, DiscardButtonLink} from './styles'
import NavigationHook from '../navigation-hook'
import {FormCtaFooterProps} from './types'

const ButtonSecondaryProgress = (
  props: Omit<React.ComponentProps<typeof ProgressButton>, 'buttonComponent'>
) => <ProgressButton buttonComponent={ButtonSecondary} {...omit(['buttonComponent'], props)} />

const FormCtaFooter: React.FC<FormCtaFooterProps> = ({
  loading,
  submitLabel = '',
  canSubmit = true,
  onSubmit,
  discardChanges,
  hasUnsavedChanges,
  onDiscard,
  lockNavigation = true,
  formIsValid = true,
  hasNavLock = true,
  disableSubmit = false,
  disableDiscard = false,
  isAlwaysVisible = false,
  hasDiscard = true,
  discardLabel,
  hasSubmit = true,
  hasBackButton = false,
  canMoveBack = false,
  onBackButtonClick,
  hasError,
  isFinalStep = true,
}) => {
  const {t} = useTranslation('charon')

  const handleSubmitClick = useCallback(
    (e?: React.UIEvent) => {
      e && e.preventDefault()
      return onSubmit()
    },
    [onSubmit]
  )

  const handleDiscardClick = useCallback(
    (e?: React.UIEvent) => {
      e && e.preventDefault()
      discardChanges && discardChanges()
      onDiscard && onDiscard()
    },
    [discardChanges, onDiscard]
  )

  return (
    <>
      <NavigationHook
        onConfirm={handleSubmitClick}
        onCancel={handleDiscardClick}
        canSubmit={!!canSubmit && !!formIsValid}
        navigationLocked={!!lockNavigation && !!hasNavLock}
        loading={!!loading}
        hasError={hasError}
        isFinalStep={isFinalStep}
      />
      {hasUnsavedChanges || isAlwaysVisible ? (
        <FormFooterContainer>
          <FooterRow hasDiscard={hasDiscard} hasSubmit={hasSubmit}>
            {hasDiscard && (
              <Col auto>
                <DiscardButtonLink
                  transparent
                  type="button"
                  data-target="discard"
                  onClick={handleDiscardClick}
                  active={!disableDiscard}
                >
                  {discardLabel || t(`sub-section-footer.cta-reset`)}
                </DiscardButtonLink>
              </Col>
            )}

            {hasSubmit && (
              <Col auto>
                <Row>
                  {hasBackButton && (
                    <ButtonSecondary
                      data-target="back-nav"
                      active={canMoveBack}
                      inverse
                      onClick={onBackButtonClick}
                    >
                      {t('sub-section-footer.cta-back')}
                    </ButtonSecondary>
                  )}
                  <Col auto>
                    {typeof loading === 'undefined' ? (
                      <ButtonSecondary
                        data-target="submit"
                        active={canSubmit}
                        onClick={handleSubmitClick}
                      >
                        {submitLabel || t(`sub-section-footer.cta-submit`)}
                      </ButtonSecondary>
                    ) : (
                      <ButtonSecondaryProgress
                        data-target="submit"
                        active={canSubmit && !disableSubmit}
                        onClick={handleSubmitClick}
                        loading={loading}
                      >
                        {submitLabel || t(`sub-section-footer.cta-submit`)}
                      </ButtonSecondaryProgress>
                    )}
                  </Col>
                </Row>
              </Col>
            )}
          </FooterRow>
        </FormFooterContainer>
      ) : (
        <BaseFormFooterContainer />
      )}
    </>
  )
}

export default FormCtaFooter
